import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { FiUpload } from "react-icons/fi";
import { appendFinanceNodeToPipeline, reconfigureNode, editFinanceNode, getPipelineByName } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './FinanceConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const FinanceConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [computedDiscomTariff, setComputedDiscomTariff] = useState(initialData.perUnitDiscomCost || '');
  // cashEquity removed (now derived on backend)
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode or reconfiguring
  const isEditing = !!initialData.name && initialData.isEditing === true;
  const isReconfiguring = initialData.isReconfiguring || false;
  
  // Log modal state for debugging
  useEffect(() => {
    console.log('FinanceConfigModal state:', {
      isOpen,
      isEditing,
      isReconfiguring,
      initialData
    });
  }, [isOpen, isEditing, isReconfiguring, initialData]);
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  // Compute and show latest tariff from catalog whenever modal opens or after save
  useEffect(() => {
    const run = async () => {
      try {
        if (!isOpen || !session?.pipeline_name || !nodeName) return;
        const doc = await getPipelineByName(session.pipeline_name);
        const catalog = (doc && doc.catalog) || {};
        const k = (suffix) => `params:${suffix}_${nodeName}`;
        const mode = String(catalog[k('discom_tariff_calc_mode')] ?? 'standard').toLowerCase();
        const x = Number(catalog[k('units_X')] ?? 0) || 0;
        const A = Number(catalog[k('energy_cost_A')] ?? 0) || 0;
        const B = Number(catalog[k('wheeling_charge_B')] ?? 0) || 0;
        const C = Number(catalog[k('electricity_duty_C')] ?? 0) || 0;
        const D = Number(catalog[k('tod_charges_D')] ?? 0) || 0;
        const E = Number(catalog[k('tax_E')] ?? 0) || 0;
        const F = Number(catalog[k('fac_F')] ?? 0) || 0;
        const G = Number(catalog[k('rebate_G')] ?? 0) || 0;
        const subsidy = Math.abs(Number(catalog[k('universal_subsidy')] ?? 0) || 0);
        const gh = (suffix) => Number(catalog[k(`gh_${suffix}`)] ?? 0) || 0;
        const subsidyPerUnit = x ? subsidy / x : 0;
        const ghComputed = (
          gh('energy_charge') +
          gh('fuel_surcharge') +
          gh('peak_surcharge') +
          (-Math.abs(gh('solar_peak_discount'))) +
          (-Math.abs(gh('pf_incentive'))) +
          (-Math.abs(gh('ehv_rebate'))) +
          gh('demand_charges') +
          gh('electricity_duty')
          - subsidyPerUnit
        );
        const computed = mode === 'gh' ? ghComputed : (x ? (A + B + C + D + E + F - G - subsidy) / x : 0);
        const rounded = Math.round(computed * 10000) / 10000;
        setComputedDiscomTariff(String(rounded));
      } catch (e) {
        // ignore
      }
    };
    run();
  }, [isOpen, session?.pipeline_name, nodeName]);



  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        return;
      }

      // No manual inputs here; computed tariff is read-only in this modal

      // cashEquity is derived in backend; no validation needed
      

      
      // Get the input node name and position from initialData
      const inputNodeName = initialData.inputNodeName;
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          // Determine which API to call based on mode
          if (isEditing) {
            // If editing, call the edit API
            console.log(`Calling API to edit finance node: ${nodeName} in pipeline: ${session.pipeline_name}`);
            console.log(`Edit finance node (no local inputs here, computed on run)`);
            
            await editFinanceNode(session.pipeline_name, nodeName, {});
            
            console.log(`Successfully edited finance node ${nodeName}`);
          } else if (isReconfiguring) {
            // If reconfiguring, call the reconfigure API
            console.log(`Calling API to reconfigure finance node: ${nodeName} in pipeline: ${session.pipeline_name}`);
            console.log(`New input node: ${inputNodeName}`);
            
            await reconfigureNode(
              session.pipeline_name,
              nodeName,
              inputNodeName
            );
            
            console.log(`Successfully reconfigured finance node ${nodeName} with new input ${inputNodeName}`);
          } else if (inputNodeName) {
            // If creating a new node, call the append API
            console.log(`Calling API to append finance node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node: ${inputNodeName}, Position: (${position.x}, ${position.y})`);
            
            await appendFinanceNodeToPipeline(session.pipeline_name, nodeName, inputNodeName, {}, position.x, position.y);
            
            console.log(`Successfully added finance node ${nodeName} to backend`);
          }
        } catch (apiError) {
          console.error('Error calling finance node API:', apiError);
          
          let actionType = 'add';
          if (isEditing) actionType = 'edit';
          if (isReconfiguring) actionType = 'reconfigure';

          const op = isEditing ? OP.node.editFinance : isReconfiguring ? OP.node.reconfigure : OP.node.appendFinance;
          const info = notifyError(apiError, { operation: op, showToast: false });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      }
      
      // Call the callback to update the UI
      onConfigured({
        name: nodeName,
        perUnitDiscomCost: computedDiscomTariff
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring finance node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => onClose({ name: nodeName, perUnitDiscomCost: computedDiscomTariff })}
      style={customStyles}
      contentLabel="Finance Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="finance-modal">
        <button onClick={() => onClose({ name: nodeName, perUnitDiscomCost: computedDiscomTariff })} className="finance-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="finance-modal-title">
          {isReconfiguring 
            ? 'Reconfigure Finance Node' 
            : isEditing
              ? 'Edit Finance Node'
              : 'Configure Finance Node'}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="finance-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="finance-modal-field">
          <label className="finance-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          <input
            type="text"
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            onBlur={() => {
              // Validate again on blur to ensure we show errors when user leaves the field
              const validation = validateNodeName(nodeName);
              setNodeNameError(validation.isValid ? '' : validation.errorMessage);
            }}
            className={`finance-modal-input ${nodeNameError ? 'error' : ''}`}
            placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
            aria-invalid={!!nodeNameError}
            aria-describedby={nodeNameError ? "node-name-error" : undefined}
            readOnly={isEditing} // Make the field read-only in edit mode
            disabled={isEditing} // Also disable it to make it visually clear
          />
          {nodeNameError && (
            <div className="finance-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!nodeNameError && !isEditing && (
            <div className="finance-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
          {isEditing && (
            <div className="finance-modal-hint" style={{ color: '#666', fontStyle: 'italic' }}>
              Node name cannot be changed in edit mode.
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '20px' }}>
          <div className="finance-modal-field" style={{ flex: 1 }}>
            <label className="finance-modal-label">
              Computed Discom Tariff (read-only):
            </label>
            <input
              type="number"
              value={computedDiscomTariff}
              readOnly
              className="finance-modal-input"
              placeholder="Computed Discom Tariff"
            />
          </div>

          {/* Cash equity is now derived in backend; keep field for UI continuity but unused in API */}
        </div>
        

        
        <div className="finance-modal-button-container">
          <button 
            className="finance-modal-configure-button"
            onClick={handleConfigure}
            disabled={isSubmitting || !!nodeNameError}
          >
            {isSubmitting 
              ? 'Processing...' 
              : isReconfiguring 
                ? 'Reconfigure' 
                : isEditing 
                  ? 'Save Changes' 
                  : 'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FinanceConfigModal;